import { AlertCircle, RefreshCw } from 'lucide-react'
import type { Language } from '@/types'

interface ErrorProps {
  title?: string
  message?: string
  onRetry?: () => void
  language?: Language
}

export function Error({
  title,
  message,
  onRetry,
  language = 'fr',
}: ErrorProps) {
  const defaultTitle =
    language === 'fr' ? 'Erreur de chargement' : language === 'ar' ? 'خطأ في التحميل' : 'Loading error'

  const defaultMessage =
    language === 'fr'
      ? 'Impossible de charger le menu. Veuillez réessayer.'
      : language === 'ar'
      ? 'تعذر تحميل القائمة. يرجى المحاولة مرة أخرى.'
      : 'Failed to load the menu. Please try again.'

  const retryText =
    language === 'fr' ? 'Réessayer' : language === 'ar' ? 'إعادة المحاولة' : 'Retry'

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {/* Error Icon */}
        <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
          <AlertCircle size={40} className="text-red-500" />
        </div>

        {/* Error Message */}
        <h2 className="text-2xl font-bold text-primary mb-3">{title || defaultTitle}</h2>
        <p className="text-muted-foreground mb-8">{message || defaultMessage}</p>

        {/* Retry Button */}
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors touch-feedback"
          >
            <RefreshCw size={20} />
            <span>{retryText}</span>
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Inline Error (for smaller spaces)
// ============================================================================

interface InlineErrorProps {
  message: string
  onRetry?: () => void
}

export function InlineError({ message, onRetry }: InlineErrorProps) {
  return (
    <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
      <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
      <p className="flex-1 text-sm text-red-700">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="p-2 rounded hover:bg-red-100 transition-colors touch-feedback"
        >
          <RefreshCw size={16} className="text-red-500" />
        </button>
      )}
    </div>
  )
}

// ============================================================================
// Error Boundary Fallback
// ============================================================================

interface ErrorBoundaryFallbackProps {
  error: Error
  resetError: () => void
}

export function ErrorBoundaryFallback({ error, resetError }: ErrorBoundaryFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-lg">
        <h1 className="text-6xl font-bold text-primary mb-4">Oops!</h1>
        <h2 className="text-xl font-semibold text-primary mb-4">
          Something went wrong
        </h2>
        <p className="text-muted-foreground mb-6">
          We apologize for the inconvenience. An error has occurred.
        </p>
        <details className="text-left mb-6">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-primary">
            Technical details
          </summary>
          <pre className="mt-2 p-4 bg-secondary/10 rounded text-xs overflow-auto">
            {error.message}
            {error.stack && (
              <>
                {'\n'}
                <span className="text-muted-foreground">{error.stack}</span>
              </>
            )}
          </pre>
        </details>
        <div className="flex gap-4 justify-center">
          <button
            onClick={resetError}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors touch-feedback"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-secondary text-primary rounded-lg hover:bg-secondary/50 transition-colors touch-feedback"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  )
}
